import React, { useContext, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AssistantOutlinedIcon from "@material-ui/icons/AssistantOutlined";
import SwapHorizOutlinedIcon from "@material-ui/icons/SwapHorizOutlined";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  canControlTicketAi,
  resumeTargets,
  ticketAiStatus
} from "../../helpers/ticketAiStatus";

const useStyles = makeStyles(theme => ({
  actionWrapper: {
    display: "flex",
    alignItems: "center",
    flex: "none"
  },
  action: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: "transparent",
    color: theme.mode === "light" ? theme.palette.primary.main : "#FFF",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.mode === "light"
        ? "rgba(8, 114, 185, 0.06)"
        : "rgba(255, 255, 255, 0.08)",
      transform: "translateY(-1px)"
    }
  }
}));

const TicketAiControl = ({ ticket, onTicketChange }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(ticket);
  const [queues, setQueues] = useState([]);
  const [destination, setDestination] = useState("");
  const [reason, setReason] = useState("");
  const canControl = canControlTicketAi(ticket, user);
  const handoff = currentTicket?.aiServiceStatus === "automation";
  const targets = resumeTargets(currentTicket, queues);
  const isFirstActivation = value =>
    Number(value?.aiServiceVersion || 0) === 0 && !value?.activePromptId;
  const resumeLabel = value => isFirstActivation(value) ? "Ativar IA" : "Retomar IA";

  const loadCurrent = async () => {
    const [ticketResponse, queueResponse] = await Promise.all([
      api.get(`/tickets/${ticket.id}`),
      api.get("/queue")
    ]);
    const fresh = ticketResponse.data;
    const availableQueues = queueResponse.data || [];
    setCurrentTicket(fresh);
    setQueues(availableQueues);
    onTicketChange(fresh);
    const defaultQueue = availableQueues.find(
      queue => Number(queue.id) === Number(fresh.queueId)
    );
    if (fresh.aiServiceStatus === "automation") {
      setDestination(defaultQueue ? String(defaultQueue.id) : "");
    } else if (defaultQueue?.promptId) {
      setDestination(String(defaultQueue.id));
    } else if (
      fresh.whatsapp?.initialAutomationStrategy === "agent" &&
      fresh.whatsapp?.promptId
    ) {
      setDestination("connection");
    } else {
      const first = availableQueues.find(queue => queue.promptId);
      setDestination(first ? String(first.id) : "");
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setReason("");
    try {
      await loadCurrent();
    } catch (error) {
      setOpen(false);
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const target = targets.find(item => item.value === destination);
    if (!handoff && !target) return;
    if (handoff && !destination) return;
    if (reason.trim().length < 3) return;
    setSaving(true);
    try {
      const path = handoff ? "handoff" : "resume";
      const queueId = handoff ? Number(destination) : target.queueId;
      const { data } = await api.post(`/tickets/${currentTicket.id}/ai/${path}`, {
        queueId,
        reason: reason.trim(),
        expectedVersion: currentTicket.aiServiceVersion
      });
      if (!data.transitioned) {
        toast.info("O atendimento já estava nesse estado.");
      } else {
        toast.success(
          handoff
            ? "IA pausada; atendimento enviado para a fila."
            : isFirstActivation(currentTicket) ? "IA ativada." : "IA retomada."
        );
      }
      onTicketChange(data.ticket);
      setOpen(false);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("O atendimento mudou. Confira o estado atualizado antes de tentar novamente.");
        try {
          await loadCurrent();
        } catch (refreshError) {
          toastError(refreshError);
        }
      } else {
        toastError(error);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {canControl && (
        <div className={classes.actionWrapper}>
          <Tooltip title={ticket.aiServiceStatus === "automation"
            ? "Transferir para atendimento humano"
            : resumeLabel(ticket)}>
            <span>
              <IconButton
                aria-label={ticket.aiServiceStatus === "automation"
                  ? "Transferir para atendimento humano"
                  : resumeLabel(ticket)}
                className={classes.action}
                size="small"
                onClick={handleOpen}
                disabled={saving}
              >
                {ticket.aiServiceStatus === "automation"
                  ? <SwapHorizOutlinedIcon />
                  : <AssistantOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {handoff
            ? "Transferir para atendimento humano"
            : isFirstActivation(currentTicket) ? "Ativar IA neste atendimento" : "Retomar automação"}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography variant="body2" gutterBottom>
                Estado atual: {ticketAiStatus(currentTicket).label}
              </Typography>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel id="ticket-ai-destination-label">
                  {handoff ? "Fila de atendimento humano" : "Agente que será usado"}
                </InputLabel>
                <Select
                  labelId="ticket-ai-destination-label"
                  value={destination}
                  onChange={event => setDestination(event.target.value)}
                  label={handoff ? "Fila de atendimento humano" : "Agente que será usado"}
                >
                  {(handoff ? queues.map(queue => ({
                    value: String(queue.id),
                    label: queue.name
                  })) : targets).map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!handoff && !targets.length && (
                <Typography color="error" variant="body2">
                  Configure um Agente de IA em uma fila ou um Agente de entrada na conexão.
                </Typography>
              )}
              {handoff && !queues.length && (
                <Typography color="error" variant="body2">
                  Configure uma Fila de transferência para atendimento humano.
                </Typography>
              )}
              <TextField
                id="ticket-ai-reason"
                label="Motivo da intervenção"
                value={reason}
                onChange={event => setReason(event.target.value)}
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                margin="normal"
                helperText="O motivo será registrado no histórico do chamado."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSave}
            disabled={
              loading || saving || !destination || reason.trim().length < 3 ||
              (!handoff && !targets.some(item => item.value === destination))
            }
          >
            {saving ? "Salvando..." : handoff ? "Transferir" : resumeLabel(currentTicket)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketAiControl;
